import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Learn from './Learn';
import Practice from './Practice';
import Play from './Play';
import Select from './Select';

interface Example {
  id: string;
  level: number;
  songId: string;
  imageFull: string;
  imageUrl: string;
  index: number;
  twisted: string[];
  text: string;
  imagePermanentLink: string;
  translation: Record<string, string>;
}

interface Song {
  id: string;
  name: string;
  artist: string;
  position: number;
  yt: string[];
  ytIndex: number;
  translation: Record<string, string>;
}

interface Lesson {
  id: string;
  level: number;
  order: number;
  content: Record<string, any>[];
}

interface Level {
  level: number;
  name: string;
  image: string;
}

interface FlyProps {
  currentLevel: number;
  currentSong: number;
  setCurrentSong: (song: number) => void;
  setCurrentLevel: (lvl: number) => void;
  currentLanguage: string;
  translate: boolean;
  examples: Example[];
  top10Songs: Song[];
  lessons: Lesson[];
  levels: Level[];
}

const Fly: React.FC<FlyProps> = ({
  currentLevel,
  currentSong,
  setCurrentSong,
  setCurrentLevel,
  currentLanguage,
  translate,
  examples,
  top10Songs,
  lessons,
  levels,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active component from route path (e.g. /learn, /practice, /play, /select)
  const path = location.pathname;
  const activeTab = path === '/' ? 'learn' : path.replace('/', '');

  const [initialIndex, setInitialIndex] = useState(0);
  

  // Reset index when level changes
  useEffect(() => {
    setInitialIndex(0);
  }, [currentLevel]);

  // Transition Handlers
  const handleLearnNext = () => {
    setInitialIndex(0);
    navigate('/practice');
  };

  const handleLearnPrevious = () => {
    // Decrement level (wrap to max level) then go to Select
    let prev = 10;
    if (levels && levels.length > 0) {
      const idx = levels.findIndex(l => l.level === currentLevel);
      if (idx !== -1) {
        prev = idx === 0 ? levels[levels.length - 1].level : levels[idx - 1].level;
      } else {
        prev = currentLevel <= 1 ? levels[levels.length - 1].level : currentLevel - 1;
      }
    } else {
      prev = currentLevel <= 1 ? 10 : currentLevel - 1;
    }
    setCurrentLevel(prev);
    navigate('/select');
  };

  const handlePracticeNext = () => {
    navigate('/play');
  };

  const handlePracticePrevious = () => {
    // Go to Learn (its last index)
    setInitialIndex(lessons.length > 0 ? lessons.length - 1 : 0);
    navigate('/learn');
  };

  const handlePlayNext = () => {
    setInitialIndex(0);
    navigate('/select');
  };

  const handlePlayPrevious = () => {
    // Go to Practice (its last index)
    setInitialIndex(examples.length > 0 ? examples.length - 1 : 0);
    navigate('/practice');
  };

  const handleSelectNext = () => {
    // Advance to next level (wrap to min level) then go to Learn
    let next = 1;
    if (levels && levels.length > 0) {
      const idx = levels.findIndex(l => l.level === currentLevel);
      if (idx !== -1) {
        next = idx === levels.length - 1 ? levels[0].level : levels[idx + 1].level;
      } else {
        next = currentLevel >= levels[levels.length - 1].level ? levels[0].level : currentLevel + 1;
      }
    } else {
      next = currentLevel >= 10 ? 1 : currentLevel + 1;
    }
    setInitialIndex(0);
    setCurrentLevel(next);
    navigate('/learn');
  };

  const handleSelectPrevious = () => {
    navigate('/play');
  };

  // Note: initialIndex is reset to 0 when `currentLevel` changes via the
  // effect above; navigation keeps the behaviour simple by changing the
  // level and navigating to the Select page.

  

  return (
    <div style={{ width: '100%', minHeight: '100vh', position: 'relative' }}>
      
      {activeTab === 'learn' && (
        <Learn
          currentLevel={currentLevel}
          currentLanguage={currentLanguage}
          translate={translate}
          lessons={lessons}
          initialIndex={initialIndex}
          onNext={handleLearnNext}
          onPrevious={handleLearnPrevious}
        />
      )}

      {activeTab === 'practice' && (
        <Practice
          currentLevel={currentLevel}
          currentSong={currentSong}
          setCurrentSong={setCurrentSong}
          currentLanguage={currentLanguage}
          translate={translate}
          examples={examples}
          top10Songs={top10Songs}
          initialIndex={initialIndex}
          onNext={handlePracticeNext}
          onPrevious={handlePracticePrevious}
        />
      )}

      {activeTab === 'play' && (
        <Play
          currentLevel={currentLevel}
          currentSong={currentSong}
          currentLanguage={currentLanguage}
          translate={translate}
          examples={examples}
          top10Songs={top10Songs}
          onNext={handlePlayNext}
          onPrevious={handlePlayPrevious}
        />
      )}

      {activeTab === 'select' && (
        <Select
          currentLevel={currentLevel}
          currentSong={currentSong}
          setCurrentSong={setCurrentSong}
          currentLanguage={currentLanguage}
          translate={translate}
          examples={examples}
          top10Songs={top10Songs}
          initialIndex={initialIndex}
          onNext={handleSelectNext}
          onPrevious={handleSelectPrevious}
        />
      )}
    </div>
  );
};

// When examples prop updates due to a level change, if pendingSetLastIndex is true
// set the initialIndex to the last available example index.
// Note: We place this effect after the component definition to keep logic grouped,
// but it needs access to state — so we'll implement it inside the component instead.

export default Fly;
